// @catalog F.030
// TabResumen — Primera pestaña del drawer rediseñado.
// Composición: chart de evolución + grid de FStats + barra horizontal
// "Costo+Margen+Descuentos" (impuestos passthrough aparte) + sparkline
// de unidades. El link "Ver desglose de inventario" vive en el banner
// inline del Drawer (justo debajo del header), no en este tab.

import { Box, Stack, Typography } from "@mui/material";
import type { DetailData, MonthlySale } from "../productDetailTypes";
import { FStat } from "./FStat";
import { TimeSeriesChart } from "./TimeSeriesChart";
import { UnitsSparkline } from "./UnitsSparkline";
import {
  fmtCurrency,
  fmtCurrencyK,
  fmtNum,
  fmtPct,
  formatMonthLabel,
} from "./helpers";

interface TabResumenProps {
  data: DetailData;
}

interface SegmentDef {
  label: string;
  value: number;
  color: string;
}

function Card({
  title,
  rightSlot,
  children,
}: {
  title?: string;
  rightSlot?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        bgcolor: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 1.25,
        p: 2,
      }}
    >
      {(title || rightSlot) && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{
            mb: 1.25,
          }}
        >
          {title && (
            <Typography
              sx={{
                fontSize: 12,
                color: "var(--ink-3)",
                fontWeight: 500,
                letterSpacing: "0.02em",
              }}
            >
              {title}
            </Typography>
          )}
          {rightSlot}
        </Stack>
      )}
      {children}
    </Box>
  );
}

export function TabResumen({ data }: TabResumenProps) {
  const totals = data.totals;
  const monthlySales: MonthlySale[] = data.monthlySales;

  // Chart data — evolución de ventas mensuales.
  // Label incluye año ("Ene 24") porque la serie cubre >12 meses y el
  // tooltip debe distinguir entre meses con mismo nombre de años distintos.
  const chartData = monthlySales.map((s) => ({
    label: formatMonthLabel(s.monthKey),
    value: Math.round(s.grossSales),
  }));

  // Barra financiera — Costo + Margen + Descuentos, todo CON IVA (Bloque 4:
  // IVA tratado como gasto del negocio, no passthrough — decisión 2026-05-27).
  const segments: SegmentDef[] = [
    {
      label: "Costo",
      value: Math.max(0, totals.totalCost),
      color: "#6b7283",
    },
    {
      label: "Margen",
      value: Math.max(0, totals.totalMargin),
      color: "#1f9d62",
    },
    {
      label: "Descuentos",
      value: Math.max(0, totals.totalDiscounts),
      color: "#c9382e",
    },
  ];
  const moneyBarTotal = segments.reduce((sum, s) => sum + s.value, 0);

  const marginPct =
    totals.totalGrossSales > 0
      ? (totals.totalMargin / totals.totalGrossSales) * 100
      : 0;

  const unitCost =
    totals.totalUnits > 0 ? totals.totalCost / totals.totalUnits : 0;

  return (
    <Stack spacing={1.75}>
      {/* Card 1 — Evolución de ventas */}
      <Card
        title={`Evolución de ventas — últimos ${monthlySales.length} meses`}
        rightSlot={
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                fontSize: 11.5,
                color: "var(--ink-3)",
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "2px",
                  bgcolor: "var(--accent)",
                }}
              />
              <span>Ventas $</span>
            </Box>
          </Stack>
        }
      >
        <TimeSeriesChart data={chartData} height={180} />
      </Card>

      {/* Card 2 — Resumen del historial (FStats) */}
      <Card title="Resumen del historial">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: "14px 18px",
          }}
        >
          <FStat
            label="Piezas totales"
            value={fmtNum(totals.totalUnits)}
            help="Unidades físicas vendidas en todo el periodo (Odoo 16 + 18)."
          />
          <FStat
            label="Ventas con IVA"
            value={fmtCurrencyK(totals.totalGrossSales)}
            help="Ventas con IVA incluido, antes de descuentos (IVA tratado como gasto del negocio)."
          />
          <FStat
            label="Margen total"
            value={fmtCurrencyK(totals.totalMargin)}
            negative={totals.totalMargin < 0}
          />
          <FStat
            label="Prom. unidades/mes"
            value={fmtNum(totals.avgMonthlyUnits)}
            sub={`${totals.monthsWithSales}/${totals.totalMonths} meses con ventas`}
          />
          <FStat
            label="Costo unitario"
            value={fmtCurrency(unitCost)}
            help="Costo de reposición de la última compra, CON IVA."
          />
          <FStat
            label="Margen %"
            value={fmtPct(marginPct)}
            negative={marginPct < 0}
            help="Margen total dividido entre ventas brutas."
          />
        </Box>
      </Card>

      {/* Card 3 — Descomposición financiera */}
      {moneyBarTotal > 0 && (
        <Card title="Descomposición financiera del historial">
          <Box
            sx={{
              display: "flex",
              height: 24,
              borderRadius: 1,
              overflow: "hidden",
              mb: 1.25,
              bgcolor: "var(--bg-2)",
            }}
          >
            {segments.map((s) => {
              const pct = (s.value / moneyBarTotal) * 100;
              return (
                <Box
                  key={s.label}
                  title={`${s.label}: ${fmtCurrencyK(s.value)} (${pct.toFixed(1)}%)`}
                  sx={{
                    width: `${pct}%`,
                    minWidth: 0,
                    bgcolor: s.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    color: "#fff",
                    fontWeight: 600,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {pct > 12 ? `${Math.round(pct)}%` : ""}
                </Box>
              );
            })}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: "10px 16px",
              fontSize: 12,
              color: "var(--ink-3)",
            }}
          >
            {segments.map((s) => (
              <Stack
                key={s.label}
                direction="row"
                spacing={0.75}
                alignItems="center"
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "2px",
                    bgcolor: s.color,
                  }}
                />
                <span>{s.label}</span>
                <Box
                  component="span"
                  sx={{
                    fontVariantNumeric: "tabular-nums",
                    fontWeight: 500,
                    color: "var(--ink-1)",
                  }}
                >
                  {fmtCurrencyK(s.value)}
                </Box>
              </Stack>
            ))}
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{ color: "var(--ink-3)" }}
            >
              <Box component="span" sx={{ fontSize: 11.5 }}>
                Impuestos (passthrough):
              </Box>
              <Box
                component="span"
                sx={{
                  fontVariantNumeric: "tabular-nums",
                  color: "var(--ink-3)",
                  fontWeight: 500,
                }}
              >
                {fmtCurrencyK(totals.totalTaxes)}
              </Box>
            </Stack>
          </Box>
        </Card>
      )}

      {/* Card 4 — Sparkline de unidades */}
      <Card title="Unidades vendidas mes a mes">
        <UnitsSparkline monthlySales={monthlySales} height={60} />
      </Card>
    </Stack>
  );
}

export default TabResumen;
